import React, { useState, useEffect } from 'react';
import { useToast } from '@/context/ToastContext';
import Card from '@/components/common/organisms/AppCard';
import Button from '@/components/common/atoms/AppButton';
import Badge from '@/components/common/atoms/Badge';
import Icon from '@/components/common/atoms/Icon';
import { uploadEmployeeDocument, getEmployeeDocuments, refreshEmployeeDocumentUrls } from '../services/employeeApiService';
import { getOnboardingStatus } from '@/features/onboarding';
import DocumentViewer from '@/components/common/organisms/DocumentViewer';

interface DocumentsTabProps {
  user: any;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ user }) => {
  const toast = useToast();
  const [documents, setDocuments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);


  useEffect(() => {
    if (user?.id) {
      loadDocuments();
    }
  }, [user]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const [empDocs, onboardingStatus] = await Promise.all([
        getEmployeeDocuments(user.id).catch(() => []),
        getOnboardingStatus(user.id).catch(() => null)
      ]);

      const normalizedEmpDocs = (empDocs || []).map((d: any) => ({
        id: d.id,
        documentType: String(d.documentType || 'Document'),
        originalFileName: d.documentName || d.originalFileName || 'Unknown Document',
        fileSizeBytes: d.fileSize || d.fileSizeBytes || 0,
        uploadedAt: d.uploadedAt,
        viewUrl: d.viewUrl,
        downloadUrl: d.downloadUrl
      }));

      let allDocs = [...normalizedEmpDocs];

      if (onboardingStatus && onboardingStatus.documents) {
        // Map onboarding documents to match the format expected by the UI
        const obDocs = onboardingStatus.documents.map((d: any) => ({
          id: d.id,
          documentType: String(d.documentType || 'Onboarding_Document'),
          originalFileName: d.documentName || d.originalFileName || 'Unknown Document',
          fileSizeBytes: d.fileSize || d.fileSizeBytes || 0,
          uploadedAt: d.uploadedAt,
          viewUrl: d.viewUrl,
          downloadUrl: d.downloadUrl
        }));

        // Filter out duplicates if any (by originalFileName and size)
        const existingSignatures = new Set(allDocs.map(d => `${d.originalFileName}_${d.fileSizeBytes}`));
        obDocs.forEach((od: any) => {
          if (!existingSignatures.has(`${od.originalFileName}_${od.fileSizeBytes}`)) {
            allDocs.push(od);
          }
        });
      }

      setDocuments(allDocs);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setIsUploading(true);
    try {
      // Default to Other. Can be extended to have a dropdown for type.
      await uploadEmployeeDocument(user.id, file, 'Other', 'Uploaded from profile page');
      toast.success('Document uploaded successfully');
      loadDocuments();
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRefreshUrl = async (documentId: string) => {
    try {
      const urls = await refreshEmployeeDocumentUrls(user.id, documentId);
      setDocuments(prevDocs =>
        prevDocs.map(doc =>
          doc.id === documentId
            ? { ...doc, viewUrl: urls.viewUrl, downloadUrl: urls.downloadUrl || doc.downloadUrl }
            : doc
        )
      );
      toast.success('Document URL refreshed');
    } catch (error: any) {
      toast.error(error.message || 'Failed to refresh document URL');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Documents</h3>
          <div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            />
            <Button variant="primary" size="md" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
              <Icon name={isUploading ? "Loader" : "Upload"} size={18} className={`mr-2 ${isUploading ? 'animate-spin' : ''}`} />
              {isUploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Icon name="Loader" className="animate-spin text-indigo-600 w-8 h-8" />
          </div>
        ) : documents.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {documents.map((doc) => (
              <div key={doc.id} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div className={`p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400`}>
                    <Icon name="FileText" size={24} />
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {String(doc.documentType).replace('_', ' ')}
                  </Badge>
                </div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate mb-1" title={doc.originalFileName}>
                  {doc.originalFileName}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  {(doc.fileSizeBytes / 1024 / 1024).toFixed(2)} MB • {new Date(doc.uploadedAt).toLocaleDateString()}
                </p>
                <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-700 flex justify-between">
                  <div className="flex space-x-3">
                    {doc.viewUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedDocument(doc);
                          setViewerOpen(true);
                        }}
                        className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline flex items-center"
                      >
                        <Icon name="Eye" size={14} className="mr-1" />
                        View
                      </button>
                    )}
                    {doc.downloadUrl && (
                      <a href={doc.downloadUrl} className="text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center">
                        <Icon name="Download" size={14} className="mr-1" /> Download
                      </a>
                    )}
                  </div>
                  <button
                    onClick={() => handleRefreshUrl(doc.id)}
                    className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline flex items-center"
                    title="Refresh URL if expired"
                  >
                    <Icon name="RefreshCw" size={14} className="mr-1" /> Refresh
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-24 h-24 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-6">
              <Icon name="FolderOpen" size={48} className="text-gray-400 dark:text-gray-600" />
            </div>
            <p className="text-gray-900 dark:text-white font-semibold mb-2">No documents available</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Employment documents will appear here once they are uploaded.
            </p>
          </div>
        )}
      </Card>

      <DocumentViewer
        open={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
          setSelectedDocument(null);
        }}
        fileUrl={selectedDocument?.viewUrl}
        fileName={selectedDocument?.originalFileName}
      />
    </div>
  );
};

export default DocumentsTab;
